import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Loader from "../Common/Loader";
import { PaymentPurchaseService, ApplyCoupon, TalkToTeam } from "../../redux/features/AuthService";
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { toast } from "react-toastify";

const formatPrice = (value) => {
  const num = parseFloat(value);
  return Number.isNaN(num) ? "0.00" : num.toFixed(2);
};

const cardStyle = {
  base: {
    color: "#32325d",
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: "antialiased",
    fontSize: "16px",
    "::placeholder": {
      color: "#aab7c4"
    }
  },
  invalid: {
    iconColor: '#fa755a',
    color: '#fa755a',
  },
  complete: {
    iconColor: '#00b300',
    color: '#00b300',
  },
};

const StripeCheckoutForm = ({selectedProduct, handleStripeCheckoutFormPopup, checkoutFormType, isEmeraldOrAbove = false}) => { // NOSONAR
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [couponCode, setcouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [couponDetail, setCouponDetail] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [productAmount, setProductAmount] = useState(selectedProduct?.price); // NOSONAR
  const [showContactModal, setShowContactModal] = useState(false);

  const ApplyStripeCoupon = async (e, planId) => {
    e.preventDefault();
    setLoading(true);

    const res = await dispatch(ApplyCoupon({ couponCode, planId }));
    if(res.type === 'auths/ApplyCoupon/fulfilled') {
      const { coupon, amount, error } = res.payload;

      if(error) { setCouponError(error) }
      if(!Number.isNaN(parseFloat(amount)) && coupon) {
        setProductAmount(parseFloat(amount));
        setCouponDetail(coupon);
      }

      setLoading(false);
    }
  }

  const RemoveStripeCoupon = async (e) => {
    e.preventDefault();

    setCouponError('');
    setCouponDetail(null);
    setProductAmount(selectedProduct?.price); // NOSONAR
  }

  const handleSubmit = async (e, planId) => { // NOSONAR
    e.preventDefault();
    if(couponError) { setcouponCode('') }
    setCouponError('');
    setLoading(true);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError("Card details are not complete.");
      setLoading(false);
      return;
    }

    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if(paymentMethodError) {
      setError("Something went wrong when trying to create the payment method. Kindly resubmit the form");
      setLoading(false);
      return;
    } else {
      const res = await dispatch(PaymentPurchaseService({ planId, paymentMethodId: paymentMethod?.id, couponCode, productAmount, checkoutFormType }));

      if(res.type === 'auths/PaymentPurchaseService/fulfilled') {
        let data = res.payload?.data;
        let user = data.userInfo;
        let clientSecret = data.clientSecret;

        if(clientSecret) {
          const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: { name: `${user.firstName} ${user.lastName}` },
            },
          });

          setLoading(false);
          handleStripeCheckoutFormPopup(false);
          if (paymentIntent) {
            window.location.replace(data.success_url);
          } else if (error) {
            window.location.replace(data.cancel_url);
          }
        } else {
          let subscription = data.subscription;

          setLoading(false);
          handleStripeCheckoutFormPopup(false);

          if (subscription?.error) {
            window.location.replace(data.cancel_url);
          } else {
            window.location.replace(data.success_url);
          }
        }
      } else {
        setLoading(false);
      }
    }
  };

  const handleTalkToTeam = async (e, planName, contactMethod) => {
    e.preventDefault();
    setLoading(true);

    const res = await dispatch(TalkToTeam({ planName, preferredContact: contactMethod }));
    if(res.type === 'auths/TalkToTeam/fulfilled') {
      toast.success(res?.payload?.message, { autoClose: 3000 });
      handleStripeCheckoutFormPopup(false);
    }

    setLoading(false);
  }

  const handleContactMethodSelect = (e, planName, contactMethod) => {
    e.preventDefault();
    setShowContactModal(false);
    handleTalkToTeam(e, planName, contactMethod);
  }

  const closeContactModal = () => {
    setShowContactModal(false);
  }

  const originalPrice = parseFloat(selectedProduct?.price) || 0;
  const finalAmount = parseFloat(productAmount) || 0;
  const savings = Math.max(0, originalPrice - finalAmount);
  const discountLabel = couponDetail?.percent_off
    ? ` (${couponDetail.percent_off}% off)`
    : couponDetail?.amount_off
      ? ` ($${formatPrice(couponDetail.amount_off / 100)} off)`
      : "";

  return (
    <>
      {loading && <Loader />}
      {selectedProduct && (
        <div className="checkout-form">
          <div className="checkout-header">
            <h2>
              {selectedProduct?.title}
              {couponDetail ? (
                <>
                  {" "}
                  <span className="text-muted text-decoration-line-through">${formatPrice(originalPrice)}</span>
                  {" "}${formatPrice(finalAmount)}
                </>
              ) : (
                <> (${formatPrice(originalPrice)})</>
              )}
            </h2>
            <strong onClick={() => {handleStripeCheckoutFormPopup(false)}}>X</strong>
          </div>
          {
          (couponDetail) ? <>
          <div className="coupon-section align-items-end coupon-container">
            <div className="w-100 mr-4">
              <strong className="card-title">{couponDetail?.name}</strong>
              <p className="card-text mb-1">
                Original price: <span className="text-decoration-line-through">${formatPrice(originalPrice)}</span>
              </p>
              <p className="card-text mb-1">
                You save: ${formatPrice(savings)}{discountLabel}
              </p>
              <p className="card-text fw-bold mb-0">
                Amount due: ${formatPrice(finalAmount)}
              </p>
            </div>
            <button type="button" className="rounded-3 btn btn-danger" onClick={(e) => {RemoveStripeCoupon(e)}}>Remove</button>
          </div>
          </> : <>
          <form className="coupon-section align-items-end" onSubmit={(e) => { ApplyStripeCoupon(e, selectedProduct?._id) }}>
            <div className="w-100 mr-4">
              <label htmlFor="coupon">Apply Coupon</label>
              {couponError && <div className="error">{couponError}</div>}
              <input
                type="text"
                id="coupon"
                value={couponCode}
                onChange={(e) => setcouponCode(e.target.value)}
                placeholder="Enter promotion code..."
              />
            </div>
            <button className="rounded-3">Apply</button>
          </form>
          </>
          }

          <form onSubmit={(e) => { handleSubmit(e, selectedProduct?._id) }}>
            <div>
              <label htmlFor="cardDetails">Enter Card Details</label>
              <div className="card-section" id="cardDetails">
                <CardElement options={{ style: cardStyle }} />
              </div>
            </div>
            {error && <div className="error">{error}</div>}
            <div className="d-flex">
              {isEmeraldOrAbove && (
                <button
                  className="rounded-3 mx-2 bg-success"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowContactModal(true);
                  }}
                  type="button"
                >
                  Talk To NTR Team
                </button>
              )}
              <button className="rounded-3" type="submit" disabled={!stripe || loading}>Pay</button>
            </div>
          </form>
        </div>
      )}

      {showContactModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={closeContactModal}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5 className="modal-title">Contact NTR Team</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeContactModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">Please select your preferred form of contact:</p>
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-outline-primary"
                    onClick={(e) => handleContactMethodSelect(e, selectedProduct?.title, 'phone')} // NOSONAR
                    type="button"
                  >
                    📞 Phone Call
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={(e) => handleContactMethodSelect(e, selectedProduct?.title, 'email')} // NOSONAR
                    type="button"
                  >
                    📧 Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
};

export default StripeCheckoutForm;
